import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMsal } from "@azure/msal-react";
import { useEffect, useState } from "react";

import { ThemeType } from "../Types/theme";
import { MySettingsContextProps } from "../Types/mySettings";

import { UserSettings } from "../../../apiclients/bstinsights";
import useBSTInsightsAPI from "../../../apiclients/api/bstinsightsapi";
import useAcquireToken from "../../../components/AuthToken/Hooks/useAcquireToken";

export const userSettingsQueryKey = "user-settings-query-key";

export const useUserSettings = () => {
  const { instance } = useMsal();
  const { getTokenSilently } = useAcquireToken();
  const { getUserSettings } = useBSTInsightsAPI();

  return useQuery({
    queryKey: [userSettingsQueryKey],
    queryFn: async () => {
      const accessToken = await getTokenSilently();
      const activeAccount = instance.getActiveAccount();
      const results = await getUserSettings(
        accessToken,
        activeAccount?.localAccountId || ""
      );
      return results;
    },
    meta: { persist: true },
    refetchOnMount: true,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
  });
};

/**
 * Initialize and Return My Settings Context values
 * @returns MySettingsContext values
 */
export const useMySettingsProvider = (): MySettingsContextProps => {
  const queryClient = useQueryClient();
  const { instance } = useMsal();
  const { data, isFetched } = useUserSettings();
  const { getTokenSilently } = useAcquireToken();
  const { updateUserSettings } = useBSTInsightsAPI();

  const [settings, setSettings] = useState<UserSettings>({
    generalUI: { theme: ThemeType.Light },
  });

  const { mutate } = useMutation({
    mutationFn: async (value: Partial<UserSettings>) => {
      const activeAccount = instance.getActiveAccount();
      const payload: UserSettings = {
        ...settings,
        azureIdentityId: activeAccount?.localAccountId || "",
        ...value,
      };
      // Optimistically update query data
      setSettings(payload);
      queryClient.setQueryData([userSettingsQueryKey], payload);
      const token = await getTokenSilently();
      if (!token) return;
      await updateUserSettings(token, payload);
    },
  });

  useEffect(() => {
    if (isFetched && data) {
      setSettings(data);
    }
  }, [data, isFetched]);

  const updateSettings = (value: Partial<UserSettings>) => mutate(value);

  const handleThemeChange = (theme: ThemeType) =>
    updateSettings({ generalUI: { theme: theme } });

  return {
    theme: {
      current: settings.generalUI?.theme as ThemeType,
      set: handleThemeChange,
    },
  };
};
